import React, { Fragment, useEffect, useState, useCallback } from 'react';
import Buttons from '../Common/Buttons';
import InvoiceForm from './InvoiceForm';
import PDFPreview from './PDFPreview';
import Footer from '../Footer/Footer';
import './InvoiceGenerator.css';


function InvoiceGeneratorMain() {
    // Mobile device detection and restriction
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => {
            const ua = navigator.userAgent || navigator.vendor || window.opera;
            if (/android|iphone|ipad|ipod|opera mini|iemobile|mobile/i.test(ua) || window.innerWidth < 700) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        document.title = "Invoice Generator | InvoicePoint";
    }, []);

    // Consent dialog always shown on every visit
    const [showConsentDialog, setShowConsentDialog] = useState(true);
    const handleConsentAgree = () => {
        setShowConsentDialog(false);
    };

    const [invoiceData, setInvoiceData] = useState({
        client: null,
        items: [],
        subtotal: 0,
        tax: 0,
        total: 0,
        paymentTerms: '14 Days',
        instructions: ''
    });
    const [pdfUrl, setPdfUrl] = useState(null);

        const [showEncourageDialog, setShowEncourageDialog] = useState(false);

    const updateInvoiceData = useCallback((data) => {
        setInvoiceData(data);
    }, []);

    if (isMobile) {
        return (
            <div className="mobile-block-modal">
                <div className="mobile-block-content">
                    <div className="mobile-block-title">Try Mobile App!</div>
                    <div className="mobile-block-body">
                        This is best experienced on desktop.<br /><br />
                        For a seamless experience on the go, download our InvoicePoint mobile app!<br />
                        <b>Enjoy instant invoice creation, payment tracking, notifications, and more—right from your phone.</b>
                    </div>
                    <div className="mobile-block-actions">
                        <Buttons stacked />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            {/* First-launch Consent Dialog */}
            {showConsentDialog && (
                <div className="consent-modal-overlay">
                    <div className="consent-modal">
                        <div className="consent-modal-content">
                            <div className="consent-modal-title">Welcome to InvoicePoint</div>
                            <div className="consent-modal-body">
                                Join thousands of satisfied small businesses using InvoicePoint.<br /><br />
                                By continuing, you agree to our
                                {' '}<a href="/terms-of-serviceterms" target="_blank" rel="noopener noreferrer" className="consent-link"><b>Terms of Use</b></a>
                                {' '}and acknowledge that you have read our
                                {' '}<a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="consent-link"><b>Privacy Policy</b></a>.
                            </div>
                            <button className="consent-modal-btn" onClick={handleConsentAgree}>
                                Agree and continue
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Encouragement Dialog */}
            {showEncourageDialog && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(0,0,0,0.35)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: 12,
                        padding: '32px 28px',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                        maxWidth: 380,
                        textAlign: 'center',
                        position: 'relative',
                    }}>
                        <button onClick={() => setShowEncourageDialog(false)} style={{
                            position: 'absolute',
                            top: 12,
                            right: 16,
                            background: 'none',
                            border: 'none',
                            fontSize: 22,
                            color: '#aaa',
                            cursor: 'pointer',
                        }}>&times;</button>
                        <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 16, color: '#27C492' }}>
                            Unlock invoice tracking, easy payment options and much more!
                        </div>
                        <div style={{ fontSize: 17, color: '#444', marginBottom: 20 }}>
                            Download Invoice Point for your mobile now to send invoices and get notified when they're viewed.
                        </div>
                        <a href="https://invoicepoint.app" style={{
                            display: 'inline-block',
                            background: '#6577E9',
                            color: '#fff',
                            padding: '10px 28px',
                            borderRadius: 6,
                            fontWeight: 500,
                            textDecoration: 'none',
                            fontSize: 16,
                        }}>
                            Get the App
                        </a>
                    </div>
                </div>
            )}
            <div className="invoice-generator-page">
                <div className="invoice-generator-header">
                    <div className="container">
                        <div className="text-center py-4">
                            <h1 className="h-2 h-bold mb-10">Invoice Generator</h1>
                            <p className="paragraph">Create professional invoices in seconds</p>
                        </div>
                    </div>
                </div>
                <div className="invoice-generator-container">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-lg-5 invoice-form-section">
                                <InvoiceForm 
                                    invoiceData={invoiceData}
                                    updateInvoiceData={updateInvoiceData}
                                    setPdfUrl={setPdfUrl}
                                />
                            </div>
                            <div className="col-lg-7 invoice-preview-section">
                                {pdfUrl ? (
                                    <div className="pdf-preview-pane" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 32 }}>
                                        <PDFPreview fileUrl={pdfUrl} />
                                        <a
                                            href={pdfUrl}
                                            download="invoice.pdf"
                                            className="btn btn-primary mt-3"
                                            style={{ marginTop: 16, padding: '8px 24px', borderRadius: 6, background: '#27C492', color: '#fff', textDecoration: 'none', fontWeight: 500 }}
                                            onClick={() => setTimeout(() => setShowEncourageDialog(true), 200)}
                                        >
                                            Download PDF
                                        </a>
                                    </div>
                                ) : (
                                    <div style={{height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#b0b0b0', fontSize: 22, fontWeight: 500, textAlign: 'center', padding: 32}}>
                                        <span>
                                            Fill the form with your profile, client, and invoice items<br/>
                                            and tap <b>Generate</b> to view the invoice preview here.
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </Fragment>
    );
}

export default InvoiceGeneratorMain;
